/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {configure} from '@instructure/moment-utils'
import timezone from 'timezone'
import en_US from 'timezone/en_US'
import {lookupDatetime} from '@canvas/i18next/localizeDate'
import i18next from '@canvas/i18next'

let onLanguageChanged: ((lng: string) => void) | null = null

declare global {
  interface Window {
    __PRELOADED_TIMEZONE_DATA__?: Record<string, unknown>
  }
}

export function up(): void {
  const tzData = window.__PRELOADED_TIMEZONE_DATA__ || {}

  let userTZ = timezone(en_US, 'en_US', 'UTC')

  // These timezones and locales should already be put on the page as <script>
  // tags from rails. this block should not create any network requests.
  if (window.ENV && ENV.TIMEZONE && tzData[ENV.TIMEZONE]) {
    userTZ = userTZ(tzData[ENV.TIMEZONE], ENV.TIMEZONE)
  }

  if (window.ENV && ENV.BIGEASY_LOCALE && tzData[ENV.BIGEASY_LOCALE]) {
    userTZ = userTZ(tzData[ENV.BIGEASY_LOCALE], ENV.BIGEASY_LOCALE)
  }

  const baseConfig = {
    tz: userTZ,
    tzData,
    momentLocale: (window.ENV && ENV.MOMENT_LOCALE) || 'en',
  }

  configure({...baseConfig, formats: getI18nFormats()})

  // This runs on canvasReadyState, which isn't synchronized with i18next's
  // own async changeLanguage() -- if that hasn't resolved to the real user
  // locale yet, getI18nFormats() snapshots English templates here and
  // nothing ever refreshes moment-utils' state, so date/time formatting
  // stays English-ordered for the rest of the session (CFA-1329). Re-run
  // the snapshot whenever i18next's language actually changes.
  if (onLanguageChanged) i18next.off('languageChanged', onLanguageChanged)
  onLanguageChanged = () => configure({...baseConfig, formats: getI18nFormats()})
  i18next.on('languageChanged', onLanguageChanged)
}

export function down(): void {
  if (onLanguageChanged) {
    i18next.off('languageChanged', onLanguageChanged)
    onLanguageChanged = null
  }
  configure({tz: undefined, tzData: undefined, momentLocale: undefined, formats: undefined})
}

export function getI18nFormats(): Record<string, string | null> {
  const keys = [
    'date.formats.date_at_time',
    'date.formats.default',
    'date.formats.full',
    'date.formats.full_with_weekday',
    'date.formats.long',
    'date.formats.long_with_weekday',
    'date.formats.medium',
    'date.formats.medium_month',
    'date.formats.medium_with_weekday',
    'date.formats.short',
    'date.formats.short_month',
    'date.formats.short_weekday',
    'date.formats.short_with_weekday',
    'date.formats.weekday',
    'time.formats.default',
    'time.formats.long',
    'time.formats.short',
    'time.formats.tiny',
    'time.formats.tiny_on_the_hour',
  ]
  return Object.fromEntries(keys.map(key => [key, lookupDatetime(key)]))
}
